import { useContext, useMemo } from "react";
import { ReconciliationSummaryResponse } from "@common/contracts/reconciliation.contract";
import { FinancialAccount } from "@common/types/financialAccount";
import { isLiabilityAccount } from "@common/helpers/financialAccount";
import { computeDisplayedKickBalances } from "@common/helpers/reconciliation";
import { ReconciliationContext } from "./Reconciliation.context";
import { toPendingCheckMergesForBalance } from "./workflowHelpers";

interface UseReconciliationBalancesParams {
    summary: ReconciliationSummaryResponse;
    financialAccount: FinancialAccount;
}

export function useReconciliationBalances({
    summary,
    financialAccount,
}: UseReconciliationBalancesParams) {
    const {
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIds,
        financialTransactionIdsIgnoringDateChanges,
        financialTransactionsFromPreviousPeriodIds,
        checkMerges,
    } = useContext(ReconciliationContext);
    const isLiability = isLiabilityAccount(financialAccount.financialType);
    const pendingCheckMerges = useMemo(
        () =>
            toPendingCheckMergesForBalance({
                checkMerges,
                outstandingCashItems: summary.outstandingCashItems,
            }),
        [checkMerges, summary.outstandingCashItems],
    );

    return useMemo(() => {
        const balances = computeDisplayedKickBalances({
            transactionsPairs: summary.transactionsPairs,
            kickOpeningDate: summary.kickOpeningDate,
            kickOpeningBalance: summary.kickOpeningBalance,
            kickOpeningBalanceDifference: summary.kickOpeningBalanceDifference,
            statementsOpeningDate: summary.statementsOpeningDate,
            statementsClosingDate: summary.statementsClosingDate,
            financialTransactionsToRemoveIds,
            statementTransactionsToAddIds,
            financialTransactionIdsIgnoringDateChanges,
            financialTransactionsFromPreviousPeriodIds,
            isLiability,
            checkMerges: pendingCheckMerges,
        });
        return {
            ...balances,
            isBalanced:
                summary.statementsClosingBalance ===
                balances.kickClosingReconciledBalance,
        };
    }, [
        summary.transactionsPairs,
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIds,
        summary.kickOpeningDate,
        summary.kickOpeningBalance,
        summary.kickOpeningBalanceDifference,
        summary.statementsOpeningDate,
        summary.statementsClosingDate,
        summary.statementsClosingBalance,
        financialTransactionIdsIgnoringDateChanges,
        financialTransactionsFromPreviousPeriodIds,
        isLiability,
        pendingCheckMerges,
    ]);
}
